import { FC, ChangeEvent, useState, useEffect, useMemo, Fragment } from 'react';
import {
  Divider,
  Box,
  FormControl,
  InputLabel,
  Card,
  Table,
  TableBody,
  TableHead,
  TablePagination,
  TableRow,
  TableContainer,
  Select,
  MenuItem,
  Typography,
  TextField,
  Grid,
  Button,
  InputAdornment,
  Tooltip,
  Checkbox,
  ListItemText
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { withStyles } from '@mui/styles';
import SearchIcon from '@mui/icons-material/Search';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { useLocation, useNavigate, useParams } from 'react-router';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { useForm } from 'react-hook-form';
import { ProjectFilterMulti } from 'src/models/project_details';
import FilterListIcon from '@mui/icons-material/FilterList';
import LinearProgress, {
  LinearProgressProps
} from '@mui/material/LinearProgress';
import {
  getProjectBilling,
  getProjectStatus,
  getAllProjectsGroupedByClients,
  searchProjectFilterGroupedByClients
} from 'src/services/projectDetailService';
import { TableColumnCell } from 'src/components/TableColumnCell';
import EditIcon from '@mui/icons-material/Edit';
import { AppActions } from 'src/store/AppReducer';
import { useDispatch } from 'react-redux';
import { downloadProjectReport } from 'src/services/projectService';
import fileDownload from 'js-file-download';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import ContextMenuPopover from './components/context-menu-popover';

interface Props {
  permissions: any;
}

const applyFilters = (projectList: any[]): any[] => {
  return projectList.filter(() => {
    let matches = true;

    return matches;
  });
};

const applyPagination = (
  projectList: any[],
  page: number,
  limit: number
): any[] => {
  return projectList.slice(page * limit, (page * limit) + limit);
};

const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};


const ProjectDetailsTable: FC<Props> = (props) => {
  function LinearProgressWithLabel(
    props: LinearProgressProps & { value: number }
  ) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress variant="determinate" {...props} />
        </Box>
        <Box sx={{ minWidth: 35 }}>
          <Typography variant="body2" color="text.secondary">{`${Math.round(
            props.value
          )}%`}</Typography>
        </Box>
      </Box>
    );
  }

  const [expand, setExpand] = useState<any>();
  const handleExpand = (code) => {
    setExpand(
      {
        id: code,
        expand: expand?.id === code ? !expand.expand : true
      }
    );
  }

  //filter project by customer
  const { customerId } = useParams();
  let query = useQuery();

  var initalList: any[] = [];
  var [projects, setProjects] = useState(initalList);

  let initialProjectFilterValues = {
    projectType: [],
    sowpoStatus: [],
    projectStatus: [],
    projectBillingCategory:[],
    searchName: ''
  } as ProjectFilterMulti;

  // removed getvalues before set value
  const { register, handleSubmit, reset, setValue ,getValues } = useForm<ProjectFilterMulti>({
    defaultValues: initialProjectFilterValues
  });

  const [toggleFilter, setToggleFilter] = useState(false);

  const [selectedProject] = useState<string[]>([]);
  const [isDisabled, setIsDisabled] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);

  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);

  const [proFilter, setProFilter] = useState(initialProjectFilterValues as ProjectFilterMulti);

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLimit(parseInt(event.target.value));
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const [filteredProject, setfilteredProject] = useState<any>(
    applyFilters(projects)
  );

  const [paginatedProject, setpaginatedProject] = useState<any>(
    applyPagination(filteredProject, page, limit)
  );

  const getAllProjectDetails = (filter?: any) => {
    dispatch(AppActions.showLoader());
    searchProjectFilterGroupedByClients(filter ? filter: initialProjectFilterValues).then((res) => {
      setProjects(res?.data || []);
      dispatch(AppActions.hideLoader());
    });
  };

  const [customerName, setCustomerName] = useState(null);

  const getProjectByCustomerId = (id) => {
    dispatch(AppActions.showLoader());
    getAllProjectsGroupedByClients().then((res) => {
      setProjects(res?.data.filter(e => e.id === id) as any[] || []);
      dispatch(AppActions.hideLoader());
    });
  };

  useEffect(() => {
    if (customerId) {
      getProjectByCustomerId(customerId);
    } else {
      getAllProjectDetails();
    }
  }, [customerId]);

  const [searchKeywords, setSearchKeywords] = useState('');

  useEffect(() => {
    setSearchKeywords(query.get('name'));
    setCustomerName(query.get('name'));
  }, [query]);

  //when user started to type the name in the text field
  const nameChange = (event) => {
    setValue('searchName', event.target.value);
    setProFilter({...proFilter, searchName: event.target.value})
    if (!customerId) {
      setSearchKeywords(event.target.value);
    } else {
      setSearchKeywords(event.target.value);
    }
  };
  

  useEffect(() => {
    setfilteredProject(applyFilters(projects));
  }, [projects, page, limit, proFilter]);

  useEffect(() => {
    setpaginatedProject(applyPagination(filteredProject, page, limit));
  }, [projects, filteredProject, page, limit]);

  const searchGroupsAll = async (filter: any) => {
    dispatch(AppActions.showLoader());
    const result = await searchProjectFilterGroupedByClients(filter);
    setPage(0);
    setProjects(result?.data);
    dispatch(AppActions.hideLoader());
  };

  useEffect(() => {
    if ((searchKeywords?.length >= 1) && !customerId) {
      searchGroupsAll(proFilter);
    }
    if (searchKeywords?.length === 0 && !customerId) {
      getAllProjectDetails();
    }
    if (searchKeywords?.length === 0 && customerId && customerName) {
      navigate('/project-details');
    }
  }, [navigate, searchKeywords, customerId, customerName, proFilter.searchName]);


  const handleClose = () => {
    setOpen(false);
  };

  const onSubmit = (data: ProjectFilterMulti) => {
    dispatch(AppActions.showLoader());
    searchProjectFilterGroupedByClients(data).then((res) => {
      setPage(0);
      setLimit(10);
      setProjects(res?.data || []);
      dispatch(AppActions.hideLoader());
    });
  };

  const resetFilterResult = () => {
    reset();
    setProFilter({...initialProjectFilterValues, searchName: searchKeywords?.length > 0 ? searchKeywords : ''});
    getAllProjectDetails({...initialProjectFilterValues, searchName: searchKeywords?.length > 0 ? searchKeywords : ''});
  };

  

  useEffect(() => {
    const filters = {
      projectType: proFilter?.projectType,
      sowpoStatus: proFilter?.sowpoStatus,
      projectStatus: proFilter?.projectStatus,
      projectBillingCategory:proFilter?.projectBillingCategory,
    };
    
    const isNullish = Object.values(filters).every((value) => {
      if (value != null) {
        if (value?.length === 0) {
          return true;
        }
      } else{
        return true;
      }
      
      return false;
    });

    if (Object.keys(filters).length === 0 || isNullish) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [proFilter]);

  const [billingCategories, setbillingCategories] = useState([]);
  const [projectStatus, setprojectStatus] = useState([]);

  const getAllProjectDropdowns = () => {
    getProjectBilling().then((res) => {
      setbillingCategories(res.data);
    });
    getProjectStatus().then((res) => {
      setprojectStatus(res.data);
    });
  };

  useEffect(() => {
    getAllProjectDropdowns();
  }, []);

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem'
    }
  })(TableRow);

  const downloadReport = () => {
    downloadProjectReport().then((res) => {
      fileDownload(res.data, 'project-report.xlsx')
    })
  };

  return (
    <div>
      <div>
        <Grid container sx={{ mt: 2, mb: 2 }} justifyContent="space-between">
          {customerId ? (<>
          <Grid style={{ display: 'flex'}}>
          <Grid item>
          <Typography
            onClick={() => {
              navigate('/segments');
            }}
              style={{
                marginLeft: '0px',
                fontWeight: '700',
                fontSize: '16px',
                fontFamily:
                  'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
                cursor: 'pointer'
              }}
            >
              Segments /
            </Typography> 
          </Grid>
          <Grid item>
          <Typography
              style={{
                paddingLeft: '5px',
                fontWeight: '700',
                fontSize: '16px',
                fontFamily:
                  'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
              }}
            > 
              {customerName ? customerName : 'Client Name'} /
            </Typography> 
          </Grid>
          <Grid item>
            <Typography
              style={{
                paddingLeft: '5px',
                fontWeight: '700',
                fontSize: '16px',
                fontFamily:
                  'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
              }}
            >
              Project Details
            </Typography>
            <Typography variant="subtitle2"></Typography>
          </Grid>
          </Grid>
          </>) : (<>
            <Grid item>
            <Typography
              style={{
                marginLeft: '0px',
                fontWeight: '700',
                fontSize: '16px',
                fontFamily:
                  'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
              }}
            >
              Project Details
            </Typography>
            <Typography variant="subtitle2"></Typography>
          </Grid>
          </>)}
          
          <Grid item>
            <Button
              id="btn-downloadReport"
              sx={{
                mr: 2,
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              startIcon={<GetAppIcon fontSize="small" />}
              onClick={() => downloadReport()}
              style={{ height: '1.75rem' }}
            >
              Download Report
            </Button>
            <Button
              id="btn-CreateNem"
              sx={{
                mt: { xs: 2, md: 0 },
                fontSize: '11px',
                fontWeight: 'normal'
              }}
              variant="contained"
              startIcon={<AddTwoToneIcon fontSize="small" />}
              onClick={() => navigate('/project-details/create-new')}
              style={{ height: '1.75rem' }}
              disabled={
                customerId ?
                props.permissions.some(
                  (perm) => perm.name === 'Create New' && perm.value === true
                ) 
                : !props.permissions.some(
                  (perm) => perm.name === 'Create New' && perm.value === true
                )
              }
            >
              Create New
            </Button>
          </Grid>
        </Grid>
      </div>
      <Card>
        <Grid sx={{ px: 2, py: 2 }} item xs={6}>
          <TextField
            id="nameChange"
            sx={{ mr: 2 }}
            size="small"
            placeholder="Project Name"
            value={searchKeywords}
            onChange={(e) => nameChange(e)}
            InputProps={{
              style: { fontSize: '12px', height: '1.75rem', width: 320 },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ color: '#d3d3d3' }}
                    style={{ fontSize: '15px' }}
                  />
                </InputAdornment>
              )
            }}
          />
          <Button
            id="btn-toggleFilter"
            sx={{
              mt: { xs: 2, md: 0 },
              color: `${!toggleFilter && 'gray'}`
            }}
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => {
              setToggleFilter(!toggleFilter);
            }}
            style={{ height: '1.75rem', fontSize: '12px' }}
          >
            Filters
          </Button>
        </Grid>
        {toggleFilter && (
          <Grid px={2} pb={2} style={{margin: 0}}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid
                container
                spacing={2}
                style={{
                  paddingTop: 3,
                  paddingBottom: 2,
                  justifyContent: 'space-between'
                }}
              >
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Project Type
                    </InputLabel>
                    <Select
                      id="projectType"
                      className="select-text"
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      sx={{ mt: 1 }}
                      fullWidth
                      size="small"
                      {...register('projectType')}
                      multiple 
                      value={proFilter.projectType || []}  
                      onChange={(event) => {
                          setProFilter((prev) => ({ ...prev, projectType: event.target.value as string[] }));
                        setValue('projectType', event.target.value as string[]);
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Escape') {
                          e.stopPropagation();
                        }
                      }}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      <MenuItem value="Pre-sale">
                      <Checkbox checked={getValues('projectType')?.includes('Pre-sale') || false} />
                        <ListItemText primary="Pre-sale" />
                      </MenuItem>
                      <MenuItem value="Delivery">
                      <Checkbox checked={getValues('projectType')?.includes('Delivery') || false} />
                        <ListItemText primary="Delivery" />
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      SOW/PO Status
                    </InputLabel>
                    <Select
                      id="sowPoStatus"
                      className="select-text"
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      sx={{ mt: 1 }}
                      fullWidth
                      multiple
                      size="small"
                      value={proFilter.sowpoStatus || []}
                      {...register('sowpoStatus')}
                          onChange={(event) => {
                        setProFilter((prev) => ({ ...prev, sowpoStatus: event.target.value as string[]  }));
                        setValue('sowpoStatus', event.target.value as string[] );
                        event.stopPropagation();
                      }}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      <MenuItem value="Approved">
                        <Checkbox  checked={proFilter.sowpoStatus?.includes('Approved') || false} /> {/* Use optional chaining to avoid errors */}
                        <ListItemText primary="Approved" />
                      </MenuItem>
                      <MenuItem value="Raised">
                        <Checkbox checked={proFilter.sowpoStatus?.includes('Raised') || false} />
                        <ListItemText primary="Raised" />
                      </MenuItem>
                      <MenuItem value="Not raised">
                        <Checkbox checked={proFilter.sowpoStatus?.includes('Not raised') || false} /> {/* Use optional chaining to avoid errors */}
                        <ListItemText primary="Not raised" />
                      </MenuItem>
                      <MenuItem value="N/A">
                        <Checkbox checked={proFilter.sowpoStatus?.includes('N/A') || false} />
                        <ListItemText primary="N/A" />
                      </MenuItem>

                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Project Status
                    </InputLabel>
                    <Select
                      id="projectStatus"
                      className="select-text"
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      sx={{ mt: 1 }}
                      fullWidth
                      size="small"
                      multiple
                      value={proFilter.projectStatus || []}
                      {...register('projectStatus')}
                      onChange={(event) => {
                      setProFilter((prev) => ({ ...prev, projectStatus: event.target.value as string[]  }));
                        setValue('projectStatus', event.target.value as string[] );
                      }}
                      renderValue={(selected) => selected.join(', ')}
                    >
                      {(projectStatus || []).map((item, i) => (
                        <MenuItem key={i} value={item.name}>
                          <Checkbox checked={proFilter.projectStatus?.includes(item.name) || false} />
                          <ListItemText primary={item.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Billing Category
                    </InputLabel>
                    <Select
                      id="projectBillingCategory"
                      className="select-text"
                      style={{
                        fontSize: '12px',
                        height: '2rem'
                      }}
                      sx={{ mt: 1 }}
                      fullWidth
                      multiple
                      size="small"
                      value={proFilter.projectBillingCategory || []}
                      {...register('projectBillingCategory')}
                      onChange={(event) => {
                        setProFilter((prev) => ({ ...prev, projectBillingCategory: event.target.value as string[]  }));
                        setValue('projectBillingCategory',event.target.value as string[] );
                      }}
                      renderValue={(selected) => selected.join(', ')}
                    >

                      {(billingCategories || []).map((item, i) => (
                        <MenuItem key={i} value={item.name}>
                          <Checkbox checked={proFilter.projectBillingCategory?.includes(item.name) || false} />
                          <ListItemText primary={item.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                container
                style={{ justifyContent: 'flex-end', marginTop: 16 }}
                xs={12}
              >
                <Button
                  id="btn-resetFilterResult"
                  sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                  variant="outlined"
                  onClick={resetFilterResult}
                  disabled={isDisabled}
                  className={isDisabled &&'clear-btn'}
                  style={{ height: '1.75rem', fontSize: '12px' }}
                >
                  Clear
                </Button>
                <Button
                  id="btn-onSubmit"
                  sx={{ mt: { xs: 2, md: 0 } }}
                  disabled={isDisabled}
                  variant="outlined"
                  className={isDisabled &&'filter-btn'}
                  type="submit"
                  style={{ height: '1.75rem', fontSize: '12px' }}
                >
                  Filter
                </Button>
              </Grid>
            </form>
          </Grid>
        )}
        <Divider />
        <TableContainer className="tableContainer">
          <Table style={{ borderCollapse: 'separate' }} size="small">
            <TableHead>
              <CustomTableRow>
                <TableHeaderCell className='tree-button' content="" smallFont/>
                <TableHeaderCell content="Clients" smallFont />
                <TableHeaderCell content="Project Code" smallFont />
                <TableHeaderCell content="Project Name" smallFont />
                <TableHeaderCell content="Project Type" smallFont />
                <TableHeaderCell content="Project Status" smallFont />
                <TableHeaderCell content="SOW/PO Status" smallFont />
                <TableHeaderCell content="Actual Start Date" smallFont />
                <TableHeaderCell content="Actual End Date" smallFont />
                <TableHeaderCell content="Pre-sale Start Date" smallFont />
                <TableHeaderCell content="Pre-sale End Date" smallFont />
                <TableHeaderCell content="Planned Hours" smallFont />
                <TableHeaderCell content="Project Completion (%)" smallFont />
                <TableHeaderCell
                  content="Project Performance Effort Overrun / (Remaining Effort)"
                  smallFont
                />
                <TableHeaderCell
                  content="Recognized Revenue Percentage of Completion based"
                  smallFont
                />
                <TableHeaderCell content="Actions" smallFont sticky />
              </CustomTableRow>
            </TableHead>

            <TableBody>
              {paginatedProject.map((customer, i) => {
                const isProjectSelected = selectedProject.includes(customer?.id);
                return (
                  <Fragment key={customer?.id}>
                    <TableRow
                      hover
                      key={customer?.id}
                      selected={isProjectSelected}
                      className={(expand?.id === customer?.id && expand?.expand) ? 'segment-row segment-selected-row' : 'segment-row'}>
                      <TableColumnCell className='tree-button'>
                        {customer?.projects?.length > 1 &&
                          <Button
                            sx={{
                              mt: { xs: 2, md: 0 },
                              fontSize: '11px',
                              fontWeight: 'normal',
                              width: '14px',
                              height: '14px',
                              padding: 0,
                              minWidth: 'unset',
                              borderRadius: '2px',
                              cursor: 'pointer'
                            }}
                            onClick={e => handleExpand(customer?.id)}
                            variant="contained"
                            startIcon={expand?.id === customer?.id && expand?.expand ? <HorizontalRuleIcon fontSize="small" style={{fontSize: '14px'}}/> : 
                            <AddTwoToneIcon fontSize="small" style={{fontSize: '14px'}}/>}
                          />
                        }
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.name}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          variant="body1"
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.key || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          variant="body1"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.name || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.type || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.status || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.sowStatus || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          color="text.primary"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.actualStartDate !== '0001-01-01T00:00:00'
                            ? customer?.projects[0]?.actualStartDate?.split('T')[0]
                            : '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          color="text.primary"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.actualEndDate !== '0001-01-01T00:00:00'
                            ? customer?.projects[0]?.actualEndDate?.split('T')[0]
                            : '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          color="text.primary"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.preSalesStartDate !== '0001-01-01T00:00:00'
                            ? customer?.projects[0]?.preSalesStartDate?.split('T')[0]
                            : '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          color="text.primary"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.preSalesEndDate !== '0001-01-01T00:00:00'
                            ? customer?.projects[0]?.preSalesEndDate?.split('T')[0]
                            : '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          color="text.primary"
                          style={{ fontSize: '11px' }}
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.plannedHours || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <div>
                          <Grid
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              pt: 1
                            }}
                          >
                            <LinearProgressWithLabel
                              variant="determinate"
                              value={
                                customer?.projects[0]?.projectCompletion >= 100
                                  ? 100
                                  : customer?.projects[0]?.projectCompletion
                              }
                              color={
                                customer?.projects[0]?.projectCompletion >= 100
                                  ? 'error'
                                  : 'primary'
                              }
                            />
                          </Grid>
                        </div>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.remainingEffort || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      <TableColumnCell>
                        <Typography
                          variant="body1"
                          paddingBottom="0.25rem"
                          paddingTop="0.5rem"
                          style={{ fontSize: '11px' }}
                          color="text.primary"
                          gutterBottom
                          noWrap
                        >
                          {customer?.projects.length === 1 ? customer?.projects[0]?.actualEffectStatus || '-' : ''}
                        </Typography>
                      </TableColumnCell>
                      {customer?.projects.length === 1 ?
                        <TableColumnCell sticky>
                          <div
                            style={{
                              background: 'white',
                              marginLeft: 'auto',
                              display: 'flex',
                              justifyContent: 'center'

                            }}
                          >{!customerId && (<>
                            { props.permissions.some(
                                    (perm) =>
                                      perm.name === 'Edit' && perm.value === true
                                  ) &&
                                  <Tooltip title="Edit" placement="top" > 
                                  <EditIcon
                                  id="btn-project-edit-view=false"
                              onClick={() => {
                                {
                                  navigate(
                                    `/project-details/project-edit/${customer?.projects[0]?.id}?client=${customer?.id}&view=false`
                                  );
                                }
                              }}
                              style={{
                                fontSize: '17px',
                                cursor: 'pointer'
                              }}
                            />
                            </Tooltip>
                            
                            }</>)}
                            {/* )} */}
                            <ContextMenuPopover
                              index={i}
                              navigateLink={`/project-details/project-edit/${customer?.projects[0]?.id}?client=${customer?.id}&view=true`}
                            />
                          </div>
                        </TableColumnCell>
                        :
                        <TableColumnCell sticky></TableColumnCell>
                      }
                    </TableRow>
                    {
                      expand?.id === customer?.id && expand?.expand && customer?.projects.length > 1 && 
                      customer?.projects.map((project, index) => {
                        return (
                          <Fragment key={project?.id}>
                            <TableRow
                              hover
                              key={project?.id}
                              className='customer-row'
                            >
                              <TableColumnCell className='tree-button'></TableColumnCell>
                              <TableColumnCell></TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  style={{ fontSize: '11px' }}
                                  variant="body1"
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.key}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  variant="body1"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.name}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.type}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.status}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.sowStatus}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  color="text.primary"
                                  style={{ fontSize: '11px' }}
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.actualStartDate !== '0001-01-01T00:00:00'
                                    ? project?.actualStartDate?.split('T')[0]
                                    : '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  color="text.primary"
                                  style={{ fontSize: '11px' }}
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.actualEndDate !== '0001-01-01T00:00:00'
                                    ? project?.actualEndDate?.split('T')[0]
                                    : '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  color="text.primary"
                                  style={{ fontSize: '11px' }}
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.preSalesStartDate !== '0001-01-01T00:00:00'
                                    ? project?.preSalesStartDate?.split('T')[0]
                                    : '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  color="text.primary"
                                  style={{ fontSize: '11px' }}
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.preSalesEndDate !== '0001-01-01T00:00:00'
                                    ? project?.preSalesEndDate?.split('T')[0]
                                    : '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  color="text.primary"
                                  style={{ fontSize: '11px' }}
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.plannedHours || '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <div>
                                  <Grid
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      pt: 1
                                    }}
                                  >
                                    <LinearProgressWithLabel
                                      variant="determinate"
                                      value={
                                        project?.projectCompletion >= 100
                                          ? 100
                                          : project?.projectCompletion
                                      }
                                      color={
                                        project?.projectCompletion >= 100
                                          ? 'error'
                                          : 'primary'
                                      }
                                    />
                                  </Grid>
                                </div>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.remainingEffort || '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body1"
                                  paddingBottom="0.25rem"
                                  paddingTop="0.5rem"
                                  style={{ fontSize: '11px' }}
                                  color="text.primary"
                                  gutterBottom
                                  noWrap
                                >
                                  {project?.actualEffectStatus || '-'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell sticky>
                                <div
                                  style={{
                                    background: 'white',
                                    marginLeft: 'auto',
                                    display: 'flex',
                                    justifyContent: 'center'

                                  }}
                                >{!customerId && (<>
                                  { props.permissions.some(
                                          (perm) =>
                                            perm.name === 'Edit' && perm.value === true
                                        ) &&
                                        <Tooltip title="Edit" placement="top" > 
                                        <EditIcon
                                        id="btn-project-edit-view=false"
                                    onClick={() => {
                                      {
                                        navigate(
                                          `/project-details/project-edit/${project?.id}?client=${customer?.id}&view=false`
                                        );
                                      }
                                    }}
                                    style={{
                                      fontSize: '17px',
                                      cursor: 'pointer'
                                    }}
                                  />
                                  </Tooltip>
                                  
                                  }</>)}
                                  {/* )} */}
                                  <ContextMenuPopover
                                    index={i}
                                    navigateLink={`/project-details/project-edit/${project?.id}?client=${customer?.id}&view=true`}
                                  />
                                </div>
                              </TableColumnCell>
                            </TableRow>
                          </Fragment>
                        );
                      })
                    }
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {paginatedProject?.length === 0 && (
          <Typography
            style={{
              fontSize: '15px',
              fontWeight: 'bold',
              margin: '1rem',
              textAlign: 'center'
            }}
            variant="body2"
          >
            No Records Found
          </Typography>
        )}
        <Box p={2}>
          <TablePagination
            sx={{
              fontSize: '12px',
              '.MuiTablePagination-displayedRows': {
                fontSize: '12px'
              },
              '.MuiTablePagination-selectLabel': {
                fontSize: '12px'
              }
            }}
            component="div"
            count={filteredProject.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[10, 20, 30, 40, 50]}
          />
        </Box>
      </Card>
    </div>
  );
};

ProjectDetailsTable.propTypes = {};

ProjectDetailsTable.defaultProps = {};

export default ProjectDetailsTable;
